import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import QuickQuestion from '../components/QuickQuestion';
import { getPricing, fmtPrice, formatSaleEnds } from '../utils/pricing';
import AwningConfigurator from '../components/AwningConfigurator';
import HowItWorks from '../components/HowItWorks';
import { fallbackProducts, fallbackAccessories } from '../prerenderData';
import './ProductDetailPage.css';
import { apiFetch } from '../services/api';
import { warrantyFor, WARRANTY_STARTS_FROM, EXPECTED_LIFE_YEARS } from '../config/warranty';
import PostcodeChecker from '../components/PostcodeChecker';
import DeliveryInfo from '../components/DeliveryInfo';
import ProjectVideo from '../components/home/ProjectVideo';
import Accordion, { AccordionRow } from '../components/Accordion';
import ProductColours from '../components/ProductColours';
import ProductDocuments from '../components/ProductDocuments';
import { TOTAL_COLOURWAYS } from '../data/sauledaColours';
import { getProductContent } from '../data/productContent';
import ProductStory from '../components/product/ProductStory';
import ProductBuyHero from '../components/product/ProductBuyHero';
import ProductGallery from '../components/product/ProductGallery';
import GalleryLink from '../components/gallery/GalleryLink';
import StickyProductBar from '../components/product/StickyProductBar';
import { cardImage } from '../utils/productImages';
import { FREIGHT_SHORT } from '../utils/delivery';

// Wind-test evidence, keyed by product slug. Only the AW01 has been
// independently wind tested (Intertek, EN 13561:2015, Class 3, tested at
// 3000mm x 1530mm). No other model - awning or pergola - carries wind
// test data, so no other model makes a wind-resistance claim.
// "AW01", "AW01 and B06", "AW01, B06 and A09" - model codes are how the range
// is known, so the first word of each product name.
function modelList(products) {
  const codes = products.map((p) => p.name.split(' ')[0]);
  return codes.length > 1 ? `${codes.slice(0, -1).join(', ')} and ${codes[codes.length - 1]}` : codes[0];
}

// An accessory's perks follow the data: which products it comes free with and
// which it fits are read from `accessory_for` (resolved by the server from
// each product's add-on list), never assumed from the category.
function accessoryPerks(product) {
  const offeredWith = product.accessory_for || [];
  const freeWith = offeredWith.filter((p) => p.included);
  return [
    freeWith.length ? `Included free with the ${modelList(freeWith)}` : null,
    offeredWith.length ? `Works with the ${modelList(offeredWith)}` : null,
    FREIGHT_SHORT,
    product.documents?.length ? 'Set-up guide free to download' : null,
    'Manufacturer warranty',
  ].filter(Boolean);
}

const WIND_TEST_BY_SLUG = {
  'aw01-full-box-awning': true,
};

function ProductDetailPage() {
  const { slug } = useParams();

  // Start from the build-time snapshot (the data the pre-rendered HTML was
  // made from) so hydration adopts that DOM; the live record replaces it as
  // soon as the fetch below resolves. A product added since the last build
  // is simply not in the snapshot and loads the ordinary way.
  const allFallback = [...fallbackProducts, ...fallbackAccessories];
  const prerenderProduct = allFallback.find(p => p.slug === slug) || null;

  const [product, setProduct] = useState(prerenderProduct);
  const [loading, setLoading] = useState(!prerenderProduct);
  // null = no variant selected → show the product's stock image
  const [activeVariantIdx, setActiveVariantIdx] = useState(null);
  const [activeSizeIdx, setActiveSizeIdx] = useState(0);

  const [related, setRelated] = useState([]);

  // Detect size-variant images by filename pattern: -<W>x<D>.webp
  // Sorted smallest → largest by footprint area.
  // Bump SIZE_VARIANT_CACHE_BUST when replacing image files to force browser re-fetch.
  const SIZE_VARIANT_CACHE_BUST = 'v5';
  const sizeVariants = React.useMemo(() => {
    if (!product?.images) return [];
    const matches = [];
    for (const img of product.images) {
      const m = img.match(/-(\d+)x(\d+)\.webp$/i);
      if (m) matches.push({ image: `${img}?${SIZE_VARIANT_CACHE_BUST}`, rawImage: img, w: +m[1], d: +m[2], label: `${m[1]}×${m[2]} m` });
    }
    matches.sort((a, b) => a.w * a.d - b.w * b.d);
    return matches;
  }, [product]);
  const sizeImageSet = React.useMemo(() => new Set(sizeVariants.map(s => s.rawImage)), [sizeVariants]);

  useEffect(() => {
    // Always fetch live data (replaces fallback on hydration)
    apiFetch(`/api/products/${encodeURIComponent(slug)}`)
      .then(r => { if (!r.ok) throw new Error('Not found'); return r.json(); })
      .then(found => {
        setProduct(found);
        // Start with no variant selected — visitor sees the stock image first.
        setActiveVariantIdx(null);
        setActiveSizeIdx(0);
        // Fetch all products to find related ones
        return apiFetch('/api/products').then(r => r.json()).then(all => {
          setRelated(all.filter(p => p._id !== found._id && p.category === found.category).slice(0, 4));
        });
      })
      .catch(() => { if (!prerenderProduct) setProduct(null); })
      .finally(() => setLoading(false));
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="pd-loading"><div className="pd-spinner"></div></div>;
  if (!product) return (
    <div className="pd-notfound">
      <h2>Product not found</h2>
      <Link to="/products">← Back to Products</Link>
    </div>
  );

  const formatCategory = (cat) => cat ? cat.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : '';

  // Same figure for both today, but read from the category so the two can
  // diverge in config without touching this page.
  const isAwning = product.category === 'awning';
  const isAccessory = product.category === 'accessory';
  // Editorial sections, spec table and FAQ - present only for products with an
  // entry in data/productContent.js. Everything else on the page is unchanged.
  const content = getProductContent(product.slug);
  // What the warranty covers - the whole awning, or a pergola's structure.
  const warranty = warrantyFor(product.category);
  // A product whose content has a `hero` gets the editorial page: the buy box
  // with the gallery beside it, then the story sections. Everything else keeps
  // the standard page below.
  const editorial = !!(content && content.hero);

  // Gallery photos. When a colour is picked in the configurator, that
  // colour's render leads the gallery so the customer sees the awning in the
  // fabric they chose.
  const photos = (product.images || []).filter((img) => !img.includes('fabric-collection') && !sizeImageSet.has(img));
  const hasVariantImages = !!(product.color_variants && product.color_variants.some((v) => v.image));
  const leadImage = hasVariantImages && activeVariantIdx !== null ? product.color_variants[activeVariantIdx]?.image : undefined;
  const selectedColourCode = activeVariantIdx != null ? product.color_variants?.[activeVariantIdx]?.code : null;
  const configureUrl = `/configure/${product.slug}${selectedColourCode ? `?colour_code=${encodeURIComponent(selectedColourCode)}` : ''}`;
  const configurable = product.category === 'awning' && product.size_matrix && product.color_variants?.length > 0;
  const awningConfigurator = configurable ? (
    <AwningConfigurator
      product={product}
      selectedColour={activeVariantIdx}
      onColourChange={setActiveVariantIdx}
    />
  ) : null;

  return (
    <div className={`pd-page${editorial ? ' pd-page--editorial' : ''}`}>
      <SEO
        title={product.name}
        description={product.short_description || product.description || `${product.name} by ASG Shading - custom-engineered for Australian conditions. Awnings include free delivery to most of Melbourne; all products are supply only.`}
        keywords={`${product.name}, pergola Melbourne, ${product.category || 'outdoor pergola'}, ASG Shading`}
        canonical={`/products/${product.slug || product._id}`}
        ogImage={product.images && product.images.length > 0 ? product.images[0] : undefined}
        ogType="product"
        noindex={!!product.noindex}
        jsonLd={[SEO.productSchema(product), SEO.breadcrumbSchema([{ name: 'Products', url: '/products' }, { name: product.name }]), ...(content && content.faq ? [SEO.faqSchema(content.faq.flatMap((g) => g.items.map((it) => ({ question: it.q, answer: it.a }))))] : []), SEO.webPageSchema({ type: 'ItemPage', name: product.name, description: product.description ? product.description.slice(0, 160) : `${product.name} by ASG Shading`, url: `/products/${product.slug || product._id}` })]}
      />
      <StickyProductBar product={product} pricing={getPricing(product)} />
      {editorial ? (
        <>
          <Breadcrumbs items={[{ name: 'Products', url: '/products' }, { name: product.name }]} />
          <ProductBuyHero
            product={product}
            hero={content.hero}
            reviews={content.reviews}
            images={photos}
            leadImage={leadImage}
            category={formatCategory(product.category)}
            configureUrl={configureUrl}
            quoteUrl={`/request-quote?product=${encodeURIComponent(product.slug)}`}
          >
            {awningConfigurator}
          </ProductBuyHero>
          <ProductStory
            content={content}
            documents={product.documents}
            faqFooter={<QuickQuestion productName={product.name} productSlug={product.slug || product._id} />}
          />
        </>
      ) : (
      <>
      <section className="pd-hero">
        <div className="pd-hero-bg"></div>
        <div className="pd-hero-content">
          {product.category && <p className="pd-label">{formatCategory(product.category)}</p>}
          <h1 className="pd-title">{product.name}</h1>
          {(() => {
            const pricing = getPricing(product);
            if (!pricing) return null;
            return (
              <>
                <p className="pd-price">
                  {pricing.original != null && (
                    <span className="pd-price-original">${fmtPrice(pricing.original)}</span>
                  )}
                  From ${fmtPrice(pricing.sale)}
                </p>
                {pricing.saleActive && pricing.saleEndsAt && (
                  <p className="pd-sale-ends">
                    {pricing.saleLabel ? `${pricing.saleLabel} - ` : ''}{formatSaleEnds(pricing.saleEndsAt)}
                  </p>
                )}
                <p className="pd-gst-note">All prices include GST.</p>
              </>
            );
          })()}
        </div>
      </section>

      <Breadcrumbs items={[{ name: 'Products', url: '/products' }, { name: product.name }]} />

      <section className="pd-content">
        <div className="pd-content-inner">
          <div className="pd-main">
            {/* Photo gallery. Swipe strip on phones, mosaic on desktop. When a
                colour is picked in the configurator, that colour's render leads
                the gallery so the customer sees the awning in their fabric. */}
            {(() => {
              const lead = leadImage;
              if (!photos.length && !lead) return null;
              return (
                <>
                  {!hasVariantImages && sizeVariants.length >= 2 && (
                    <div className="pd-sizes">
                      <div className="pd-size-hero">
                        <img
                          src={sizeVariants[activeSizeIdx]?.image || sizeVariants[0].image}
                          alt={`${product.name} - ${sizeVariants[activeSizeIdx]?.label || sizeVariants[0].label} footprint`}
                          width={800}
                          height={600}
                          loading="eager"
                          key={activeSizeIdx}
                        />
                      </div>
                      <div className="pd-size-chips" role="tablist" aria-label="Size variation">
                        {sizeVariants.map((sv, i) => (
                          <button
                            key={sv.label}
                            type="button"
                            role="tab"
                            aria-selected={i === activeSizeIdx}
                            className={`pd-size-chip ${i === activeSizeIdx ? 'pd-size-chip--active' : ''}`}
                            onClick={() => setActiveSizeIdx(i)}
                          >{sv.label}</button>
                        ))}
                      </div>
                    </div>
                  )}
                  <ProductGallery images={photos} leadImage={lead} name={product.name} />
                  {!isAccessory && <GalleryLink productSlug={product.slug} className="pd-gallery-link" />}
                  {product.images.some((img) => img.includes('fabric-collection')) && (
                    <div className="pd-fabric-palette">
                      <img
                        src={product.images.find((img) => img.includes('fabric-collection'))}
                        alt={`${product.name} - available fabric colours`}
                        width={800}
                        height={400}
                        loading="lazy"
                      />
                    </div>
                  )}
                </>
              );
            })()}

            {/* Everything below the buy area lives in collapsible rows: the
                full detail is one tap away without a long scroll, and every
                section is visible as a heading at a glance. Rows render their
                children only while open, so the video does not mount (or
                autoplay) until asked for. */}
            <Accordion className="pd-accordion">
              {product.description && (
                <AccordionRow title="Description">
                  <p className="pd-acc-text">{product.description}</p>
                  {!isAccessory && <GalleryLink productSlug={product.slug} className="pd-acc-gallery-link" />}
                </AccordionRow>
              )}

              {((product.dimensions && product.dimensions.length > 0) || (product.specs && product.specs.length > 0)) && (
                <AccordionRow title="Specifications" id="specifications">
                  {product.dimensions && product.dimensions.length > 0 && (
                    <table className="pd-dimensions-table">
                      <tbody>
                        {product.dimensions.map((d, i) => (
                          <tr key={i}>
                            <th>{d.spec}</th>
                            <td>{d.value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  {product.specs && product.specs.length > 0 && (
                    <ul className="pd-specs-list">
                      {product.specs.map((spec, i) => (
                        <li key={i}>
                          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path d="M9 12.75 11.25 15 15 9.75" />
                          </svg>
                          {spec}
                        </li>
                      ))}
                    </ul>
                  )}
                </AccordionRow>
              )}

              {isAwning && (
                <AccordionRow title="Fabric colours" subtitle={`${TOTAL_COLOURWAYS} Sauleda colourways`}>
                  <ProductColours />
                </AccordionRow>
              )}

              {WIND_TEST_BY_SLUG[product.slug] && (
                <AccordionRow title="Wind test" subtitle="EN 13561 Class 3, tested by Intertek">
                  <p className="pd-windtest-headline">
                    <strong>EN 13561 Class 3</strong> - wind tested by Intertek. Rated to
                    Beaufort force 6 (39 to 49 km/h). Tested at 3000mm x 1530mm. Retract in
                    stronger wind.
                  </p>
                  <p className="pd-windtest-note">
                    Wind resistance results apply to the sample and size tested. Full Intertek
                    test report available on request.
                  </p>
                </AccordionRow>
              )}

              {product.video_mp4 && (
                <AccordionRow title="See it in action" lazy>
                  <ProjectVideo
                    video={{
                      mp4: product.video_mp4,
                      webm: product.video_webm || '',
                      poster: product.video_poster || (product.images && product.images[0]) || '',
                      alt: `${product.name} in operation`,
                    }}
                    title={product.name}
                  />
                </AccordionRow>
              )}

              <AccordionRow
                title="Warranty"
                subtitle={isAccessory
                  ? 'Manufacturer warranty'
                  : `${warranty.years} years from the ${WARRANTY_STARTS_FROM}`}
              >
                {/* An accessory is an electrical component, and electrical
                    components have always been covered by their maker, not
                    by ASG's warranty - see /terms clause 11. */}
                <p className="pd-acc-text">
                  {isAccessory ? (
                    <>
                      This is an electrical component and carries its manufacturer&apos;s warranty,
                      separate from ASG&apos;s warranty on the product it fits.
                    </>
                  ) : (
                    <>
                      {warranty.summary}
                      {isAwning && ` It is built to last ${EXPECTED_LIFE_YEARS} years or more - that is its expected life, not a warranty term.`}
                    </>
                  )}
                </p>
                <p className="pd-acc-text">
                  Excludes damage from misuse, unauthorised modifications, extreme weather beyond
                  design loads, or normal wear of moving components.
                  {!isAccessory && ' The Dooya motor and other electrical components are covered by their own manufacturer warranties, separate from this warranty.'}
                </p>
                <p className="pd-acc-text">
                  ASG is supply only, so the warranty covers the product as supplied - not
                  installation. <Link to="/terms">View full terms and conditions.</Link>
                </p>
              </AccordionRow>

              <AccordionRow title="Delivery" subtitle="Free to most of Melbourne">
                <DeliveryInfo />
              </AccordionRow>
            </Accordion>

            {/* Supporting material sits under the accordion in the content column
                rather than in the buy box, which keeps the sidebar short enough to
                stay sticky and the content column the longer of the two. */}
            {/* Directly below the trust list. Renders nothing when the product
                has no documents on disk. */}
            <ProductDocuments documents={product.documents} />

            {isAwning && (
              <div className="pd-guides">
                <Link to="/measuring-guide" className="pd-guide-link">
                  How to measure for your awning
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M5 12h14m0 0-6-6m6 6-6 6" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </Link>
                <Link to="/for-installers" className="pd-guide-link">
                  Information for your installer
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M5 12h14m0 0-6-6m6 6-6 6" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </Link>
              </div>
            )}

            <QuickQuestion productName={product.name} productSlug={product.slug || product._id} />


          </div>

          <div className="pd-sidebar">
            <PostcodeChecker />
            {/* Awning configurator replaces the generic sidebar card for awning products */}
            {configurable ? (
              <>
                <Link to={configureUrl} className="pd-configure-btn">
                  Customise your awning
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M5 12h14m0 0-6-6m6 6-6 6" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </Link>
                {awningConfigurator}
              </>
            ) : (
            <div className="pd-sidebar-card">
              {(() => {
                const pricing = getPricing(product);
                const buyable = pricing && product.active !== false;
                if (buyable) {
                  return (
                    <>
                      <h3>Ready to buy?</h3>
                      <p>From <strong>${fmtPrice(pricing.sale)}</strong> incl. GST. Free delivery to most of Melbourne, $150 flat to the outer suburbs, freight quoted elsewhere - check your postcode above.</p>
                      <Link
                        to={`/checkout?product=${encodeURIComponent(product.slug || product._id)}`}
                        className="pd-sidebar-btn pd-sidebar-btn--buy"
                        rel="nofollow"
                      >Buy Now</Link>
                      <Link to="/request-quote" className="pd-sidebar-link">Or request a custom quote →</Link>
                    </>
                  );
                }
                // Products with no price are enquiry-only. The wording has to
                // follow the category: a pergola is quoted to a builder, an
                // accessory is quoted against the awning it goes on.
                const enquiryCopy = isAccessory
                  ? 'This accessory is supplied with the awning it fits. Tell us which model you have and we will quote it.'
                  : 'Pergolas are supply-only. Request a quote and we\'ll prepare full specifications for your builder.';
                return (
                  <>
                    <h3>Interested in this product?</h3>
                    <p>{enquiryCopy}</p>
                    <Link to="/request-quote" className="pd-sidebar-btn">Request a Quote</Link>
                  </>
                );
              })()}
              {/* The perks describe what ASG supplies, which is not the same
                  promise for a structure and for a bolt-on accessory. */}
              <div className="pd-sidebar-perks">
                {(isAccessory
                  ? accessoryPerks(product)
                  : ['Built to your specs', FREIGHT_SHORT, 'Full specifications for your builder', `${warranty.label}*`]
                ).map((perk) => (
                  <div className="pd-perk" key={perk}>
                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path d="M9 12.75 11.25 15 15 9.75" /></svg>
                    {perk}
                  </div>
                ))}
              </div>
            </div>
            )}

            <div className="pd-sidebar-footer">
              {product.updatedAt && (
                <p className="pd-updated">Last updated: {new Date(product.updatedAt).toLocaleDateString('en-AU', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
              )}
              <Link to="/products" className="pd-back">← All Products</Link>
            </div>
          </div>
        </div>
      </section>

      {product.category === 'awning' && (
        <section className="pd-hiw">
          <HowItWorks compact awningsOnly />
        </section>
      )}
      </>
      )}

      {/* Related products - internal cross-linking */}
      {related.length > 0 && (
        <section className="pd-related">
          <div className="pd-related-inner">
            <h2>Related Products</h2>
            <div className="pd-related-grid">
              {related.map(r => (
                <Link to={`/products/${r.slug || r._id}`} className="pd-related-card" key={r._id}>
                  {cardImage(r) && (
                    <img src={cardImage(r)} alt={r.name} width={400} height={267} loading="lazy" />
                  )}
                  <h3>{r.name}</h3>
                  {(() => {
                    const rp = getPricing(r);
                    if (!rp) return null;
                    return (
                      <span className="pd-related-price">
                        {rp.original != null && (
                          <span className="pd-related-price-original">${fmtPrice(rp.original)}</span>
                        )}
                        From ${fmtPrice(rp.sale)}
                      </span>
                    );
                  })()}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

export default ProductDetailPage;
